import { useState, useMemo } from 'react';
import type { ModHistoryEntry } from '@/mocks/adminModeracion';

interface Props {
  history: ModHistoryEntry[];
}

const targetTypeIcons: Record<string, string> = {
  mensaje: 'ri-chat-3-line',
  usuario: 'ri-user-line',
  ip: 'ri-global-line',
  palabra: 'ri-font-size',
  sistema: 'ri-robot-2-line',
};

const targetTypeColors: Record<string, string> = {
  mensaje: 'bg-blue-100 text-blue-600',
  usuario: 'bg-violet-100 text-violet-600',
  ip: 'bg-red-100 text-red-600',
  palabra: 'bg-amber-100 text-amber-600',
  sistema: 'bg-emerald-100 text-emerald-600',
};

export default function ModHistoryLog({ history }: Props) {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('todos');

  const types = ['todos', 'usuario', 'mensaje', 'ip', 'palabra', 'sistema'];

  const filtered = useMemo(() => {
    return history.filter((h) => {
      if (typeFilter !== 'todos' && h.targetType !== typeFilter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        h.admin.toLowerCase().includes(q) ||
        h.action.toLowerCase().includes(q) ||
        h.target.toLowerCase().includes(q) ||
        h.details.toLowerCase().includes(q)
      );
    });
  }, [history, search, typeFilter]);

  const formatDateTime = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const isYesterday = new Date(now.getTime() - 86400000).toDateString() === d.toDateString();
    const time = d.toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' });
    if (isToday) return `Hoy, ${time}`;
    if (isYesterday) return `Ayer, ${time}`;
    return d.toLocaleDateString('es-CR', { day: 'numeric', month: 'short' }) + `, ${time}`;
  };

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar en historial..."
            className="w-full pl-8 pr-8 py-2 rounded-xl bg-background-50 text-foreground-800 text-xs border border-background-200 focus:border-secondary-300 focus:ring-1 focus:ring-secondary-200 outline-none transition-all placeholder:text-foreground-400"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-foreground-300 flex items-center justify-center cursor-pointer hover:bg-foreground-400 transition-colors">
              <i className="ri-close-line text-background-50 text-[8px]"></i>
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-none">
          {types.map((t) => (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              className={`px-2.5 py-1.5 rounded-full text-[10px] font-semibold whitespace-nowrap cursor-pointer transition-all ${
                typeFilter === t ? 'bg-secondary-500 text-background-50' : 'bg-background-100 text-foreground-500 hover:bg-background-200'
              }`}
            >
              {t === 'todos' ? 'Todos' : t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
        <span className="text-[10px] text-foreground-400">{filtered.length} registros</span>
      </div>

      {/* Timeline */}
      <div className="bg-background-50 border border-background-200 rounded-xl">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center py-12 gap-2">
            <div className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-search-line text-foreground-300 text-lg"></i>
            </div>
            <p className="text-xs text-foreground-400 font-medium">Sin registros</p>
          </div>
        ) : (
          <div className="divide-y divide-background-100">
            {filtered.map((entry, idx) => (
              <div key={entry.id} className="flex items-start gap-3 px-4 py-3.5 hover:bg-background-50 transition-colors">
                {/* Timeline dot & line */}
                <div className="shrink-0 flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${targetTypeColors[entry.targetType]}`}>
                    <i className={`${targetTypeIcons[entry.targetType]} text-xs`}></i>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-semibold text-foreground-900">{entry.action}</span>
                    <span className="text-[9px] text-foreground-400">
                      {entry.admin === 'Sistema' ? (
                        <span className="inline-flex items-center gap-0.5 text-emerald-600 font-medium">
                          <i className="ri-robot-2-line text-[9px]"></i>
                          Sistema
                        </span>
                      ) : (
                        entry.admin
                      )}
                    </span>
                  </div>
                  <p className="text-[10px] text-foreground-600">
                    <strong className="text-foreground-800">{entry.target}</strong>
                    {' '}&mdash;{' '}
                    {entry.details}
                  </p>
                </div>

                {/* Time */}
                <span className="text-[10px] text-foreground-400 whitespace-nowrap shrink-0">
                  {formatDateTime(entry.timestamp)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}